import { useMutation, useQuery } from "@tanstack/react-query"
import { Form, Spin } from "antd"
import dayjs from "dayjs"
import { Button, ModalWrapper } from "components"
import { AntDesignInput } from "components/antd/AntDesignInput"
import AntDesignSelect from "components/antd/AntDesignSelect"
import React, { useState, useEffect } from "react"
import { toast } from "react-toastify"
import { AUTH_ENDPOINTS, authService } from "services/authService"
import { COMMON_ENDPOINTS, commonService } from "services/commonService"
import { useProfileStore } from "stores/profile"
import { onQueryError } from "utils/utils"
import { ININData } from "../Profile"
import { queryClient } from "index"
import { useAppNotificationStore } from "stores"
import { trackEvent } from "utils/analytics"

const EditPersonalDetails: React.FC<{
  closeModal: Function
  PersonData: ININData
  isAdmin: boolean
}> = ({ closeModal, PersonData, isAdmin }) => {
  const { getProfile } = useProfileStore()
  const { toast } = useAppNotificationStore()
  const [form] = Form.useForm()
  const [dataBody, setDataBody] = useState({
    date_of_birth_month: "",
    date_of_birth_day: "",
    date_of_birth_year: "",
    mobile_phone: "",
    display_phone_number: "",
  })
  // Kept so the save can tell whether anything actually changed.
  const [initialBody, setInitialBody] = useState(dataBody)

  const month = Form.useWatch("month", form)
  const day = Form.useWatch("day", form)
  const year = Form.useWatch("year", form)

  const missingDateValue = !month || !day || !year

  useEffect(() => {
    const body = {
      date_of_birth_month: String(PersonData?.date_of_birth_month_id),
      date_of_birth_day: String(PersonData?.date_of_birth_day_id),
      date_of_birth_year: String(PersonData?.date_of_birth_year),
      mobile_phone: String(PersonData?.mobile_phone),
      display_phone_number: String(PersonData?.mobile_phone),
    }
    setDataBody(body)
    setInitialBody(body)
  }, [PersonData])
  const { isLoading: IsLoadingBirthMonth, data: birthMonth } = useQuery<
    IBaseRes<IBirthdayMonth[]>
  >({
    queryKey: [COMMON_ENDPOINTS.GET_BIRTHDAY_MONTH],
    queryFn: () => commonService.getBirthdayMonth(),
  })
  const { isLoading: IsLoadingDays, data: birthDays } = useQuery<
    IBaseRes<IBirthdayDay[]>
  >({
    queryKey: [COMMON_ENDPOINTS.GET_BIRTHDAY_DAY],
    queryFn: () => commonService.getBirthdayDay(),
  })
  const { isLoading: IsLoadingYears, data: birthYears } = useQuery<
    IBaseRes<IBirthdayYear[]>
  >({
    queryKey: [COMMON_ENDPOINTS.GET_BIRTHDAY_YEAR],
    queryFn: () => commonService.getBirthdayYear(),
  })

  // Days in a month - 29 in February only on a leap year. 31 until both are known.
  const TotalDays = (month?: string, year?: string) => {
    const monthVal = Number(month)
    const yearVal = Number(year)
    return monthVal && yearVal
      ? dayjs(
          `${yearVal}-${String(monthVal).padStart(2, "0")}-01`
        ).daysInMonth()
      : 31
  }

  // Days the selected month has. From dataBody because the form holds "Mar", not "March".
  const maxDay = TotalDays(
    dataBody.date_of_birth_month,
    dataBody.date_of_birth_year
  )

  // Reset the selected day when the month has fewer days.
  const resetDay = (days: number) => {
    if (parseInt(form.getFieldValue("day"), 10) > days) {
      form.setFieldValue("day", "")
      setDataBody((prev) => ({ ...prev, date_of_birth_day: "" }))
    }
  }

  const { isLoading: isLoading, mutate: submit } = useMutation({
    mutationFn: (body: any) =>
      isAdmin
        ? authService.updateDetails(body)
        : authService.employeeUpdateDetails(body),
    onSuccess: (data) => {
      // Whether this lands as a direct change or a pending request depends on
      // the company's approval toggle, which the server decides. The event
      // marks the submission either way - the approval outcome is a separate,
      // server-fired event.
      trackEvent("profile_edit_submitted", { section: "personal_details" })
      getProfile()
      closeModal()
      data?.message && toast.success(data.message)
      queryClient.invalidateQueries({
        queryKey: isAdmin
          ? [AUTH_ENDPOINTS.VIEW_PROFILE]
          : [AUTH_ENDPOINTS.EMPLOYEE_VIEW_PROFILE],
      })
    },
    onError: onQueryError,
  })
  const onFinish = () => {
    // Nothing edited - close without calling the API.
    if (JSON.stringify(dataBody) === JSON.stringify(initialBody)) {
      closeModal()
      return
    }
    submit({
      ...dataBody,
    })
  }
  return (
    <ModalWrapper
      title="Change your personal details"
      subTitle="We’ll use this information to quickly and securely verify your identity over the phone when you call our team. Rest assured, your information is in good hands."
      width={700}
      onClose={() => {
        closeModal()
      }}
    >
      <>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            day: PersonData?.date_of_birth_day_alias,
            month: PersonData?.date_of_birth_month_alias,
            year: PersonData?.date_of_birth_year_alias,
            number: PersonData?.mobile_phone,
          }}
        >
          <div className="w-full  p-5 grid grid-cols-2 gap-x-6">
            <AntDesignSelect
              label="Month"
              name="month"
              required
              isLoading={IsLoadingBirthMonth}
              onChange={(selected) => {
                const selectedOption = birthMonth?.data?.find(
                  (option: any) => option.description === selected
                )

                setDataBody({
                  ...dataBody,
                  date_of_birth_month: String(selectedOption?.monthId),
                })
                resetDay(
                  TotalDays(
                    String(selectedOption?.monthId),
                    dataBody.date_of_birth_year
                  )
                )
              }}
              options={birthMonth?.data?.map((option: any) => ({
                label: option.description,
                value: option.description,
              }))}
              placeholder="Please select"
            />
            <AntDesignSelect
              label="Day"
              name="day"
              required
              isLoading={IsLoadingDays}
              onChange={(selected) => {
                const selectedOption = birthDays?.data?.find(
                  (option: any) => option.description === selected
                )

                setDataBody({
                  ...dataBody,
                  date_of_birth_day: String(selectedOption?.monthDayId),
                })
              }}
              options={birthDays?.data
                ?.filter((option: any) => Number(option.description) <= maxDay)
                .map((option: any) => ({
                  label: option.description,
                  value: option.description,
                }))}
              placeholder="Please select"
            />
            <AntDesignSelect
              label="Year"
              name="year"
              required
              isLoading={IsLoadingYears}
              onChange={(selected) => {
                const selectedOption = birthYears?.data?.find(
                  (option: any) => option.text === selected
                )

                setDataBody({
                  ...dataBody,
                  date_of_birth_year: String(selectedOption?.id),
                })
                resetDay(
                  TotalDays(
                    dataBody.date_of_birth_month,
                    String(selectedOption?.id)
                  )
                )
              }}
              options={birthYears?.data?.map((option: any) => ({
                label: option.text,
                value: option.text,
              }))}
              placeholder="Please select"
            />
            <AntDesignInput
              name="number"
              label="Mobile number"
              onChange={(e) => {
                const number = e.target.value.replace(/\D/g, "")
                form.setFieldValue("number", number)
                setDataBody({
                  ...dataBody,
                  mobile_phone: number,
                  display_phone_number: number,
                })
              }}
            />
          </div>
          <div className="flex justify-end items-center gap-4 p-5">
            <Button
              type="button"
              onClick={() => {
                closeModal()
              }}
              color="neutral"
              title="Previous"
              className="h-fit text-[#5D6B82]"
            >
              Cancel{" "}
            </Button>
            <Button
              type="submit"
              color={isLoading ? "secondary" : "primary"}
              title="update"
              disabled={isLoading || missingDateValue}
              className="rounded-5px disabled:opacity-50 focus:outline-none focus:shadow-none disabled:cursor-not-allowed py-[7px] px-4 text-sm min-w-[106px] bg-primary500 hover:bg-primary700 active:bg-primary800 focus:bg-primary800 text-white shadow-none h-auto"
            >
              {isLoading ? <Spin></Spin> : "Save"}
            </Button>
          </div>
        </Form>
      </>
    </ModalWrapper>
  )
}

export default EditPersonalDetails

interface IBirthdayMonth {
  monthId: number
  description: string
  alias: string
}

interface IBirthdayDay {
  monthDayId: number
  description: string
  alias: string
}

interface IBirthdayYear {
  id: number
  text: string
}
